import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import { RemoveMyPersonalDocumentsService, GetMyPersonalDocService } from '../../../services/ProfileService';
import { download } from '../../../utils/FileDownload';
import DataTableComponentFrontPagination from '../../common/DataTableComponentFrontPagination';
import Tablefilter from '../../common/Tablefilter';
import { StandardButton } from '../../common/GlobalElements/StandardButton';
import { FileExtensionTag } from '../../library/FileExtensionTag';
import { tokens } from '../../../public/tokens';
import styled from 'styled-components';

const FILE_TYPE_OPTIONS = [
    { label: 'PDF', value: 'PDF' },
    { label: 'PNG', value: 'PNG' },
    { label: 'JPEG', value: 'JPEG' },
];

function getFileExtension(fileName = '') {
    const ext = fileName.split('.').pop()?.toUpperCase();

    if (ext === 'JPG') return 'JPEG';

    return ext;
}

function getFileIconClass(fileName = '') {
    const ext = getFileExtension(fileName);
    switch (ext) {
        case 'PDF':
            return 'fat fa-file-pdf';
        case 'PNG':
            return 'fat fa-file-png';
        case 'JPEG':
            return 'fat fa-file-jpg';
        default:
            return 'fat fa-file';
    }
}

const FileLink = styled.a({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: tokens.colors.text.link,
    fontFamily: tokens.globals['font-family'],
    fontSize: tokens.typography.fontSize['lg-14px'],
    fontWeight: tokens.typography.fontWeights.bold,
    lineHeight: '23px',
});

export default function ProfilePersonalDocuments({ docType }) {
    const [personalDocuments, setPersonalDocuments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [fileTypeFilter, setFileTypeFilter] = useState({ arr: [], checkObj: {} });
    const file = useSelector((state) => state.file);

    useEffect(() => {
        let mounted = true;
        setLoading(true);
        GetMyPersonalDocService({ dt: docType })
            .then((res) => {
                if (mounted) {
                    setPersonalDocuments(res.data);
                    setLoading(false);
                }
            })
            .catch(() => {
                if (mounted) setLoading(false);
            });

        return function cleanup() {
            setPersonalDocuments([]);
            mounted = false;
        };
    }, [file, docType]);

    const cleanFileUrl = (url = '') => url.replace(/([^:]\/)\/+/g, '$1');

    const handleDownload = (e, item) => {
        if (e) e.preventDefault();
        download(cleanFileUrl(item.file), item.fileName);
    };

    const handleDownloadAll = async () => {
        for (const doc of personalDocuments) {
            await download(cleanFileUrl(doc.file), doc.fileName, true, false);
        }
    };

    const downloadAllButton = (
        <StandardButton
            key="download-all"
            type="button"
            variant="primary"
            onClick={handleDownloadAll}
            title="Download All"
            disabled={!personalDocuments.length}
        >
            <i className="fal fa-download"></i>
            Download All
        </StandardButton>
    );

    const columns = useMemo(
        () => [
            {
                name: 'Name',
                selector: (row) => row.fileName,
                sortable: true,
                wrap: true,
                left: true,
                cell: (row) => (
                    <FileLink
                        href={row.file}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={row.fileName}
                        className="d-flex align-items-center gap-2"
                    >
                        <i className={getFileIconClass(row.fileName)}></i>
                        <span>{row.fileName}</span>
                    </FileLink>
                ),
            },
            {
                name: 'Type',
                selector: (row) => getFileExtension(row.fileName),
                sortable: true,
                cell: (row) => <FileExtensionTag fileType={getFileExtension(row.fileName)} />,
                left: true,
            },
            {
                name: 'Actions',
                cell: (row) => (
                    <button
                        style={{ border: 'none', background: 'none', marginRight: '16px' }}
                        type="button"
                        title="Download"
                        onClick={(e) => handleDownload(e, row)}
                    >
                        <i className="fal fa-folder-open"></i>
                    </button>
                ),
                ignoreRowClick: true,
                right: true,
            },
        ],
        [],
    );

    const filteredData = useMemo(() => {
        let result = personalDocuments;

        if (fileTypeFilter.arr.length) {
            result = result.filter((doc) => fileTypeFilter.arr.includes(getFileExtension(doc.fileName)));
        }

        if (search) {
            const searchQuery = search.toLowerCase();
            result = result.filter(
                (doc) =>
                    doc.fileName.toLowerCase().includes(searchQuery) || doc.type.toLowerCase().includes(searchQuery),
            );
        }

        return result;
    }, [personalDocuments, search, fileTypeFilter]);

    const filters = [
        {
            filterName: 'File Type',
            optionArr: FILE_TYPE_OPTIONS,
            state: fileTypeFilter,
            setState: setFileTypeFilter,
            uniqueId: 'fileType',
            singleSelect: true,
        },
    ];

    return (
        <DataTableComponentFrontPagination
            data={filteredData}
            columns={columns}
            keyField="id"
            search={search}
            setSearch={setSearch}
            loading={loading}
            changePage={() => {}}
            exportData={[]}
            exportFileName={''}
            filters={filters}
            tableButton={[downloadAllButton]}
            isExport={false}
            isInsidePopUp={true}
            defaultSort={{ defaultSortColumn: 'fileName', defaultSortAsc: true }}
        />
    );
}
